import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Rating,
  TextField,
  Alert,
  Divider,
  Chip,
} from '@mui/material';
import axios from 'axios';
import { format } from 'date-fns';

import { API_URL } from '../constants/urls';

interface PropositionScoringProps {
  propositionId: string;
  organizationId: string;
  readOnly?: boolean;
}

interface ScoringData {
  obvious_choice_score: number | null;
  obvious_choice_score_updated_at: string | null;
  proof_of_concept_score: number | null;
  proof_of_concept_evidence: string | null;
  proof_of_concept_updated_at: string | null;
  scale_evidence_score: number | null;
  scale_evidence_evidence: string | null;
  scale_evidence_updated_at: string | null;
  market_recognition_score: number | null;
  market_recognition_evidence: string | null;
  market_recognition_updated_at: string | null;
  customer_advocacy_score: number | null;
  customer_advocacy_evidence: string | null;
  customer_advocacy_updated_at: string | null;
  competitive_differentiation_score: number | null;
  competitive_differentiation_evidence: string | null;
  competitive_differentiation_updated_at: string | null;
}

const scoringComponents = [
  {
    key: 'proof_of_concept',
    label: 'Proof of Concept',
    description: 'Demonstrated working solution with real customers',
  },
  {
    key: 'scale_evidence',
    label: 'Scale Evidence',
    description: 'Evidence of ability to scale across customers',
  },
  {
    key: 'market_recognition',
    label: 'Market Recognition',
    description: 'Awards, analyst coverage, thought leadership',
  },
  {
    key: 'customer_advocacy',
    label: 'Customer Advocacy',
    description: 'References, case studies, testimonials',
  },
  {
    key: 'competitive_differentiation',
    label: 'Competitive Differentiation',
    description: 'Unique capabilities competitors lack',
  },
];

export default function PropositionScoring({
  propositionId,
  organizationId,
  readOnly = false,
}: PropositionScoringProps) {
  const [scoring, setScoring] = useState<ScoringData | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editedScoring, setEditedScoring] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = React.useRef(true);
  const abortControllerRef = React.useRef<AbortController | null>(null);

  useEffect(() => {
    isMountedRef.current = true;

    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    fetchScoring();

    return () => {
      isMountedRef.current = false;
      // Cancel the request when component unmounts or dependencies change
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [propositionId, organizationId]);

  const fetchScoring = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/propositions/${propositionId}/scoring?organization_id=${organizationId}`,
        {
          signal: abortControllerRef.current?.signal,
        },
      );

      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setScoring(response.data);
        setEditedScoring(response.data);
      }
    } catch (err: any) {
      // Ignore abort errors - they're expected when switching organizations
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        return;
      }

      // Only log and update state if component is still mounted
      if (isMountedRef.current) {
        // Don't log 404 errors as they're expected when switching organizations
        if (err.response?.status !== 404) {
          // console.error removed
        }
        setError('Failed to load scoring data');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleScoreChange = (component: string, value: number | null) => {
    // Allow setting to 0 explicitly
    const newValue = value === null ? 0 : value;
    setEditedScoring({
      ...editedScoring,
      [`${component}_score`]: newValue,
    });
  };

  // Calculate score dynamically while editing
  const calculateEditScore = () => {
    let total = 0;
    scoringComponents.forEach((comp) => {
      const scoreKey = `${comp.key}_score`;
      const score = editedScoring[scoreKey];
      if (typeof score === 'number') {
        total += score;
      }
    });
    return total; // Each component is 0-2, so total is 0-10
  };

  const handleEvidenceChange = (component: string, value: string) => {
    setEditedScoring({
      ...editedScoring,
      [`${component}_evidence`]: value,
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Send all fields to ensure proper updates (including 0 values)
      const changedFields: any = {};
      scoringComponents.forEach((comp) => {
        const scoreKey = `${comp.key}_score`;
        const evidenceKey = `${comp.key}_evidence`;

        // Always include score fields to handle 0 values properly
        if (scoreKey in editedScoring) {
          changedFields[scoreKey] = editedScoring[scoreKey] ?? 0;
        }

        // Include evidence if it exists
        if (
          evidenceKey in editedScoring &&
          editedScoring[evidenceKey] !==
            scoring?.[evidenceKey as keyof ScoringData]
        ) {
          changedFields[evidenceKey] = editedScoring[evidenceKey];
        }
      });

      await axios.put(
        `${API_URL}/propositions/${propositionId}/scoring?organization_id=${organizationId}`,
        changedFields,
      );

      await fetchScoring();
      setEditMode(false);
    } catch (err) {
      // console.error removed
      setError('Failed to save scoring data');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedScoring(scoring);
    setEditMode(false);
    setError(null);
  };

  if (loading) {
    return <Typography>Loading scoring data...</Typography>;
  }

  return (
    <Card>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h6">Proposition Strength Score</Typography>
          {!editMode ? (
            !readOnly && (
              <Button variant="outlined" onClick={() => setEditMode(true)}>
                Edit Scoring
              </Button>
            )
          ) : (
            <Box>
              <Button onClick={handleCancel} sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Overall Score Display */}
        <Box textAlign="center" mb={4}>
          <Typography variant="h2" color="primary" fontWeight="bold">
            {editMode
              ? calculateEditScore().toFixed(1)
              : scoring?.obvious_choice_score?.toFixed(1) || '—'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            out of 10
          </Typography>
          {!editMode && scoring?.obvious_choice_score_updated_at && (
            <Typography variant="caption" color="text.secondary">
              Last updated:{' '}
              {format(
                new Date(scoring.obvious_choice_score_updated_at),
                'MMM d, yyyy',
              )}
            </Typography>
          )}
          {editMode && (
            <Typography variant="caption" color="text.secondary">
              Score updates as you edit
            </Typography>
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Scoring Components */}
        <Box display="flex" flexDirection="column" gap={3}>
          {scoringComponents.map((component) => {
            const scoreKey = `${component.key}_score` as keyof ScoringData;
            const evidenceKey =
              `${component.key}_evidence` as keyof ScoringData;
            const updatedKey =
              `${component.key}_updated_at` as keyof ScoringData;

            const score = editMode
              ? editedScoring[scoreKey]
              : scoring?.[scoreKey];
            const evidence = editMode
              ? editedScoring[evidenceKey]
              : scoring?.[evidenceKey];
            const updatedAt = scoring?.[updatedKey];

            return (
              <Box key={component.key}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {component.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {component.description}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={2}>
                    {editMode ? (
                      <Rating
                        value={score ?? 0}
                        max={2}
                        onChange={(_, value) =>
                          handleScoreChange(component.key, value)
                        }
                        size="large"
                        precision={1}
                      />
                    ) : (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          label={`${score || 0}/2`}
                          color={
                            score === 2
                              ? 'success'
                              : score === 1
                                ? 'warning'
                                : 'default'
                          }
                          size="small"
                        />
                        {updatedAt && (
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(updatedAt as string), 'MMM d')}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
                {editMode ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    variant="outlined"
                    placeholder="Add evidence to support this score..."
                    value={evidence || ''}
                    onChange={(e) =>
                      handleEvidenceChange(component.key, e.target.value)
                    }
                    sx={{ mt: 1 }}
                  />
                ) : (
                  evidence && (
                    <Box mt={1} p={1} bgcolor="grey.50" borderRadius={1}>
                      <Typography variant="body2">{evidence}</Typography>
                    </Box>
                  )
                )}
              </Box>
            );
          })}
        </Box>

        {/* Scoring Guide */}
        <Box mt={4} p={2} bgcolor="info.lighter" borderRadius={1}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Scoring Guide
          </Typography>
          <Typography variant="body2">
            <strong>0 points:</strong> No evidence or capability
          </Typography>
          <Typography variant="body2">
            <strong>1 point:</strong> Some evidence, emerging capability
          </Typography>
          <Typography variant="body2">
            <strong>2 points:</strong> Strong evidence, mature capability
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}
